/**
 * ConversationThread — Outlook / Gmail-style threaded conversation view.
 *
 * Shows every message in a conversation stacked chronologically (oldest first),
 * with earlier messages as collapsed cards (avatar + sender + preview +
 * timestamp) and the currently-selected message expanded with full HTML body.
 *
 * Click any collapsed card to expand it. Click the expanded header to collapse.
 *
 * Props:
 *   current   — the selected email (always rendered fully expanded)
 *   thread    — array of earlier/later messages in the same conversation
 *   formatDate — function used by the parent to format timestamps
 */
import { useMemo, useState } from 'react';
import { Paperclip } from 'lucide-react';

const _TONES = [
  'bg-amber-400 text-white',
  'bg-rose-400 text-white',
  'bg-emerald-400 text-white',
  'bg-sky-400 text-white',
  'bg-indigo-400 text-white',
  'bg-purple-400 text-white',
  'bg-orange-400 text-white',
  'bg-teal-400 text-white',
];
function avatarTone(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h << 5) - h + seed.charCodeAt(i);
  return _TONES[Math.abs(h) % _TONES.length];
}

function Avatar({ name, email, size = 32 }) {
  const initial = (name || email || '?').replace(/[^a-zA-Z]/g, '').slice(0, 1).toUpperCase() || '?';
  const px = size + 'px';
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold flex-shrink-0 ${avatarTone(email || name)}`}
      style={{ width: px, height: px, fontSize: Math.round(size * 0.42) }}
    >
      {initial}
    </span>
  );
}

export default function ConversationThread({ current, thread, formatDate }) {
  // Chronological ascending timeline = thread + current, deduped by id.
  // The "latest/selected" message is always expanded; earlier ones are
  // collapsed by default (click to expand).
  const ordered = useMemo(() => {
    const merged = [...(thread || []), current].filter(Boolean);
    const seen = new Set();
    const deduped = [];
    for (const m of merged) {
      const key = m.id || (m.received_at + '|' + (m.from_email || ''));
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(m);
    }
    // Sort ascending by received_at / sent_at / created_at
    return deduped.sort((a, b) => {
      const ta = a.received_at || a.sent_at || a.created_at || '';
      const tb = b.received_at || b.sent_at || b.created_at || '';
      return ta.localeCompare(tb);
    });
  }, [current, thread]);

  // Expanded-by-id set. Start with current email expanded.
  const [expanded, setExpanded] = useState(() => new Set([current?.id]));

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-2" data-testid="conversation-thread">
      {ordered.map((msg) => {
        const isExpanded = expanded.has(msg.id);
        const isCurrent = msg.id === current.id;
        const senderName = msg.from_name || msg.from_email || 'Unknown';
        const isSelf = msg.folder === 'sent' || msg.is_sent === true;
        const displaySender = isSelf ? 'You' : senderName;
        return (
          <div
            key={msg.id || msg.received_at}
            className={`rounded-lg border overflow-hidden transition-colors ${
              isCurrent
                ? 'border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 shadow-sm'
                : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60'
            }`}
            data-testid={`conversation-message-${msg.id}`}
          >
            {/* ── Header (always visible, click to toggle) ────────────── */}
            <button
              type="button"
              onClick={() => toggle(msg.id)}
              className={`w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${
                isCurrent ? '' : 'hover:bg-slate-100 dark:hover:bg-slate-800/80'
              }`}
              data-testid={`conversation-toggle-${msg.id}`}
            >
              <Avatar name={senderName} email={msg.from_email} size={36} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                    {displaySender}
                  </span>
                  {msg.has_attachments && (
                    <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                  )}
                  {isCurrent && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold">
                      Latest
                    </span>
                  )}
                </div>
                {isExpanded ? (
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 space-y-0.5">
                    {!isSelf && (
                      <div className="truncate">From: {msg.from_email}</div>
                    )}
                    {msg.to_recipients?.length > 0 && (
                      <div className="truncate">To: {msg.to_recipients.join(', ')}</div>
                    )}
                    {msg.cc_recipients?.length > 0 && (
                      <div className="truncate">CC: {msg.cc_recipients.join(', ')}</div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">
                    {msg.body_preview || '(no preview)'}
                  </p>
                )}
              </div>
              <span className="text-[11px] text-slate-400 whitespace-nowrap flex-shrink-0">
                {formatDate(msg.received_at || msg.sent_at || msg.created_at)}
              </span>
            </button>

            {/* ── Expanded body ───────────────────────────────────────── */}
            {isExpanded && (
              <div className="border-t border-slate-200 dark:border-slate-700">
                {msg.body_html ? (
                  <div
                    className="prose prose-sm max-w-none dark:prose-invert email-body-container px-4 py-3"
                    style={{ colorScheme: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: msg.body_html }}
                  />
                ) : (
                  <div className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                    {msg.body_preview || '(no content)'}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
